import { create } from 'zustand';
import api from '../lib/api';
import i18n from '../i18n';

interface User {
  id: number;
  email: string;
  name: string | null;
  phone: string | null;
  role: string;
  plan: string;
  email_verified?: number;
  created_at: string;
}

// Výsledek prvního kroku loginu: buď přihlášeno, nebo je potřeba 2FA kód.
export interface LoginResult {
  twofaRequired: boolean;
  method?: 'totp' | 'email';
  twofaToken?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, remember?: boolean, turnstileToken?: string) => Promise<LoginResult>;
  register: (email: string, password: string, name?: string, turnstileToken?: string) => Promise<void>;
  verifyTwofa: (twofaToken: string, code: string) => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  logout: () => void;
  loadUser: () => Promise<void>;
  updateUser: (data: Partial<User>) => void;
}

// Odolné čtení z localStorage — „undefined"/„null"/poškozený JSON nesmí shodit celou
// appku (JSON.parse('undefined') hází SyntaxError). Při poškození se sám vyčistí.
function readStoredUser(): User | null {
  const raw = localStorage.getItem('user');
  if (!raw || raw === 'undefined' || raw === 'null') return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    localStorage.removeItem('user');
    return null;
  }
}

function readStoredToken(): string | null {
  const t = localStorage.getItem('token');
  if (!t || t === 'undefined' || t === 'null') {
    if (t) localStorage.removeItem('token');
    return null;
  }
  return t;
}

// Uloží jen validní hodnoty (jinak by se do localStorage dostal string „undefined").
function persistAuth(user: User | null | undefined, token: string | null | undefined) {
  if (token) localStorage.setItem('token', token); else localStorage.removeItem('token');
  if (user) localStorage.setItem('user', JSON.stringify(user)); else localStorage.removeItem('user');
}

export const useAuthStore = create<AuthState>((set) => ({
  user: readStoredUser(),
  token: readStoredToken(),
  isAuthenticated: !!readStoredToken(),
  isLoading: false,

  login: async (email, password, remember = false, turnstileToken) => {
    const { data } = await api.post('/auth/login', { email, password, remember, turnstile_token: turnstileToken });
    // 2FA zapnuté → server nevydá token, ale výzvu k zadání kódu.
    if (data?.twofa_required) {
      localStorage.setItem('auth_remember', remember ? '1' : '0');
      return { twofaRequired: true, method: data.method, twofaToken: data.twofa_token };
    }
    if (!data?.token || !data?.user) throw new Error('Neplatná odpověď serveru (chybí token/user).');
    persistAuth(data.user, data.token);
    // Příznak pro idle-logout: „zapamatovat" = bez automatického odhlášení při neaktivitě.
    localStorage.setItem('auth_remember', remember ? '1' : '0');
    set({ user: data.user, token: data.token, isAuthenticated: true });
    return { twofaRequired: false };
  },

  // Dokončení loginu po zadání 2FA kódu (TOTP / e-mail / recovery).
  verifyTwofa: async (twofaToken, code) => {
    const { data } = await api.post('/auth/2fa/verify-login', { twofa_token: twofaToken, code });
    if (!data?.token || !data?.user) throw new Error('Neplatná odpověď serveru (chybí token/user).');
    persistAuth(data.user, data.token);
    set({ user: data.user, token: data.token, isAuthenticated: true });
  },

  register: async (email, password, name, turnstileToken) => {
    const ref = localStorage.getItem('ref_code') || undefined;
    const { data } = await api.post('/auth/register', { email, password, name, lang: i18n.language || 'cs', ref, turnstile_token: turnstileToken });
    if (!data?.token || !data?.user) throw new Error('Neplatná odpověď serveru (chybí token/user).');
    persistAuth(data.user, data.token);
    localStorage.removeItem('ref_code'); // pozvánka uplatněna
    set({ user: data.user, token: data.token, isAuthenticated: true });
  },

  // Přihlášení existujícím tokenem (sociální login, guest checkout) — uloží token a dotáhne uživatele.
  loginWithToken: async (token) => {
    localStorage.setItem('token', token);
    set({ token, isAuthenticated: true });
    try {
      const { data } = await api.get('/auth/me');
      if (data?.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
        set({ user: data.user });
      }
    } catch { /* token uložen; uživatel se dotáhne při příští akci */ }
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('auth_remember');
    set({ user: null, token: null, isAuthenticated: false });
  },

  loadUser: async () => {
    try {
      set({ isLoading: true });
      const { data } = await api.get('/auth/me');
      if (data?.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
        set({ user: data.user, isLoading: false });
      } else {
        set({ isLoading: false });
      }
    } catch {
      set({ isLoading: false });
    }
  },

  updateUser: (data) => {
    set((state) => {
      const updated = { ...state.user, ...data } as User;
      localStorage.setItem('user', JSON.stringify(updated));
      return { user: updated };
    });
  },
}));
